import Image from "next/image";
import React, {
  useEffect,
  useState,
  useReducer,
  useCallback,
  useRef,
} from "react";
import ReactTooltip from "react-tooltip";
import useSWRImmutable from "swr/immutable";
import MiniSearch from "minisearch";
import MapSwitcher from "../../../../components/races/national/map/MapSwitcher";
import MapResultsCandidates from "./MapResultsCandidates";
import MapCumulativeChart from "../CumulativeChart";
import MapTransmissionChart from "./MapTransmissionChart";
import MapChartLegend from "../ChartLegend";
import CloseIcon from "../../../../../public/images/icons/close-icon.svg";
import styles from "../../../../styles/races/national/MapResults.module.scss";
import MapTimeSlider from "./MapTimeSlider";
import { maxBy, minBy, groupBy, max, min } from "lodash";
import {
  getFormattedDateTime,
  getFriendlyFormattedDateTime,
  getTransmissionPercentage,
} from "../../../../helpers/util";
import { WidgetShare } from "../../../SocialButtons";

const reducer = (state, { type, slug, mutation }) => {
  if (type === "set") {
    return mutation;
  } else if (type === "update") {
    const candidateIndex = state.findIndex(
      (candidate) => candidate.wp_slug === slug
    );
    if (candidateIndex === -1) {
      return [...state];
    }

    state[candidateIndex] = {
      ...state[candidateIndex],
      ...mutation,
    };

    if (mutation.votes) {
      state.sort((a, b) => b.votes - a.votes);
    }

    return [...state];
  } else {
    return [...state];
  }
};

const cfUrl = process.env.API_CF_URL;
const basePath = process.env.SITE_URL;
const MapResults = ({
  presidential,
  vPresidential,
  url,
  regionList,
  provincesList,
  cityList,
  philippines,
  isEmbedShowed = false,
  locationViceData = [],
  locationPresData = [],
}) => {
  const TOTAL_PRECINCTS_PH = regionList
    ? (regionList || [])
        .map((region) => (region || {}).precincts_count)
        .reduce((a, b) => a + b, 0)
    : null;
  const TOTAL_PRECINCTS_OAV = (
    regionList.find((region) => region.slug === "oav") || {}
  ).precincts_count;
  // const TIMELAPSE_START_DATE = new Date(
  //   "May 9 2022 17:00:00 GMT+0800"
  // ).getTime();
  // const TIMELAPSE_END_DATE = new Date(
  //   "May 11 2022 18:00:00 GMT+0800"
  // ).getTime();
  // const TIMELAPSE_INTERVAL_CHART = 60 * 60 * 1000; // 1 hour
  const TIMELAPSE_INTERVAL_MAP = 1 * 60 * 1000;
  const ELECTION_DAY_TRANSMISSION_START = 1652094000000;

  const miniSearch = new MiniSearch({
    fields: ["name", "display_name"],
    storeFields: ["name", "display_name", "slug", "province", "region"],
  });
  const [presidentialList, setPresidentialList] = useReducer(reducer, [
    ...presidential.results,
  ]);
  const [vPresidentialList, setVPresidentialList] = useReducer(reducer, [
    ...vPresidential.results,
  ]);
  const [candidateDataLoading, setCandidateDataLoading] = useState(true);
  const [candidateDataError, setCandidateDataError] = useState("");
  const [mapType, setMapType] = useState("votes");
  const [raceType, setRaceType] = useState("presidential");
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedRegionCopy, setSelectedRegionCopy] = useState(null);
  const [regionFilter, setRegionFilter] = useState("");
  const [regionFilterActive, setRegionFilterActive] = useState(false);
  const [selectedProvinceICity, setSelectedProvinceICity] = useState(null);
  const [selectedProvinceICityCopy, setSelectedProvinceICityCopy] =
    useState(null);
  const [provinceICityFilter, setProvinceICityFilter] = useState("");
  const [provinceICityFilterActive, setProvinceICityFilterActive] =
    useState(false);
  const [candidateFilter, setCandidateFilter] = useState(["all", "all"]);
  const [tooltipContent, setTooltipContent] = useState({
    title: "",
    description: "",
  });
  const [isMounted, setIsMounted] = useState(false);
  const [selectedMap, setSelectedMap] = useState("Philippines");
  const [showCandidateList, setShowCandidateList] = useState(false);
  const [currentMapState, setCurrentMapState] = useState([]);
  const [currentCandidateList, setCurrentCandidateList] = useState([]);
  const [lastTransmissionDate, setLastTransmissionDate] = useState(null);
  const [locationSlug, setLocationSlug] = useState("philippines");
  const [mapTimeRange, setMapTimeRange] = useState({
    minTimestamp: 0,
    maxTimestamp: 0,
    markerInterval: 0,
  });
  const [locationData, setLocationData] = useState({});
  const [selectedTimeStep, setSelectedTimeStep] = useState(0);
  const [timeSliderManuallyChanged, setTimeSliderManuallyChanged] =
    useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [locationsData, setLocationsData] = useState([...locationPresData]);
  const [snapshotData, setSnapshotData] = useState([...locationPresData]);
  const tooltipRef = useRef(null);
  const mouseCoords = { x: 0, y: 0 };
  const slugList = [...provincesList, ...regionList, ...cityList].map(
    (region) => region.slug
  );
  const _raceType =
    raceType === "presidential"
      ? "PRESIDENT PHILIPPINES"
      : "VICE PRESIDENT PHILIPPINES";
  const fetcher = (url, body) =>
    fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body,
    }).then(async (res) => {
      return res.json();
    });

  // const oldFetcher = (url) => fetch(url).then((res) => res.json());

  // const { data: locationsData, error: locationsDataError } = useSWR(
  //   `/api/redis/snapshot-results?locationSlugs=${slugList.join(
  //     ","
  //   )},philippines&raceType=${_raceType}&interval=${TIMELAPSE_INTERVAL_MAP}`,
  //   oldFetcher,
  //   {
  //    refreshInterval: 0,
  //     shouldRetryOnError: true,
  //     revalidateOnReconnect: true,
  //     refreshWhenOffline: true,
  //     errorRetryInterval: 5000,
  //     errorRetryCount: 1,
  //   }
  // );

  // const { data: snapshotData, error: snapshotError } = useSWR(
  //   `/api/redis/snapshot-results?locationSlugs=${slugList.join(
  //     ","
  //   )},philippines&raceType=${_raceType}&interval=1000`,
  //   oldFetcher,
  //   {
  //    refreshInterval: 0,
  //     shouldRetryOnError: true,
  //     revalidateOnReconnect: true,
  //     refreshWhenOffline: true,
  //     errorRetryInterval: 5000,
  //     errorRetryCount: 1,
  //   }
  // );
  // const { data: locationsData, error: locationsDataError } = useSWR(
  //   [
  //     `${cfUrl}/snapshotResults`,
  //     JSON.stringify({
  //       data: {
  //         locationSlugs: 'all',
  //         raceType: _raceType,
  //         interval: TIMELAPSE_INTERVAL_MAP
  //       }
  //     })
  //   ],
  //   fetcher,
  //   {
  //     refreshInterval: 0,
  //     shouldRetryOnError: true,
  //     revalidateOnReconnect: true,
  //     refreshWhenOffline: true,
  //     errorRetryInterval: 5000,
  //     errorRetryCount: 1,
  //   }
  // );

  // const { data: snapshotData, error: snapshotError } = useSWR(
  //   [
  //     `${cfUrl}/snapshotResults`,
  //     JSON.stringify({
  //       data: {
  //         locationSlugs: 'all',
  //         raceType: _raceType,
  //         interval: 1000
  //       }
  //     })
  //   ],
  //   fetcher,
  //   {
  //     refreshInterval: 0,
  //     shouldRetryOnError: true,
  //     revalidateOnReconnect: true,
  //     refreshWhenOffline: true,
  //     errorRetryInterval: 5000,
  //     errorRetryCount: 1,
  //   }
  // );

  const {
    data: rawCumulativeVotesChartData,
    error: cumulativeVotesChartError,
  } = useSWRImmutable(
    [
      `${cfUrl}/PHCumulativeVotes`,
      JSON.stringify({
        data: {
          raceType: _raceType,
        },
      }),
    ],
    fetcher,
    {
      refreshInterval: 0,
      shouldRetryOnError: true,
      revalidateOnReconnect: true,
      refreshWhenOffline: true,
      errorRetryInterval: 5000,
      errorRetryCount: 1,
    }
  );
  const cumulativeVotesChartData = rawCumulativeVotesChartData?.result;

  const { data: rawLatestResultsPHData, error: latestResultsPHError } =
    useSWRImmutable(
      [
        `${cfUrl}/latestTransmissionCount`,
        JSON.stringify({
          data: {
            locationSlug: "philippines",
          },
        }),
      ],
      fetcher,
      {
        refreshInterval: 0,
        shouldRetryOnError: true,
        revalidateOnReconnect: true,
        refreshWhenOffline: true,
        errorRetryInterval: 5000,
        errorRetryCount: 1,
      }
    );
  const latestResultsPHData = rawLatestResultsPHData?.result;

  const { data: rawLatestResultsOAVData, error: latestResultsOAVError } =
    useSWRImmutable(
      [
        `${cfUrl}/latestTransmissionCount`,
        JSON.stringify({
          data: {
            locationSlug: "oav",
          },
        }),
      ],
      fetcher,
      {
        refreshInterval: 0,
        shouldRetryOnError: true,
        revalidateOnReconnect: true,
        refreshWhenOffline: true,
        errorRetryInterval: 5000,
        errorRetryCount: 1,
      }
    );
  const latestResultsOAVData = rawLatestResultsOAVData?.result;

  const {
    data: rawLatestLocationResultData,
    error: latestLocationResultError,
  } = useSWRImmutable(
    [
      `${cfUrl}/latestLocationResults`,
      JSON.stringify({
        data: {
          locationSlug,
          raceType: _raceType,
        },
      }),
    ],
    fetcher,
    {
      refreshInterval: 0,
      shouldRetryOnError: true,
      revalidateOnReconnect: true,
      refreshWhenOffline: true,
      errorRetryInterval: 5000,
      errorRetryCount: 1,
    }
  );
  const latestLocationResultData = rawLatestLocationResultData?.result;

  useEffect(() => {
    setIsMounted(true);
    const updateCandidateData = async () => {
      setLocationSlug(
        selectedMap === "OAV"
          ? "oav"
          : selectedProvinceICity
          ? selectedProvinceICity.slug
          : selectedRegion
          ? selectedRegion.slug
          : "philippines"
      );

      const _raceType =
        raceType === "presidential"
          ? "PRESIDENT PHILIPPINES"
          : "VICE PRESIDENT PHILIPPINES";
      try {
        let candidateData = currentCandidateList;
        if (!currentCandidateList?.length) {
          if (raceType === "presidential") {
            setPresidentialList({
              type: "set",
              mutation: [...presidential.results],
            });
          } else {
            setVPresidentialList({
              type: "set",
              mutation: [...vPresidential.results],
            });
          }
        } else {
          if (candidateData && candidateData.length) {
            candidateData = candidateData
              .sort((a, b) => b.voteCount - a.voteCount)
              .map((items, i) => ({
                rank: i + 1,
                ...items,
              }));
          }

          candidateData.forEach((dataPoint) => {
            const mutation = {
              rank: dataPoint.rank,
              votes: dataPoint.voteCount,
              percentage: dataPoint.votePercentage,
            };

            const params = {
              type: "update",
              slug: dataPoint.candidateSlug,
              mutation,
            };

            if (raceType === "presidential") {
              setPresidentialList(params);
            } else {
              setVPresidentialList(params);
            }
          });
        }
      } catch (err) {
        return err;
      }
    };

    setCandidateDataLoading(true);
    updateCandidateData()
      .then(() => {
        setCandidateDataError("");
        setCandidateDataLoading(false);
      })
      .catch((err) => {
        setCandidateDataError(err);
        setCandidateDataLoading(false);
      });
  }, [
    selectedRegion,
    selectedProvinceICity,
    raceType,
    selectedMap,
    latestLocationResultData,
    latestResultsOAVData,
    currentCandidateList,
  ]);

  /** When snapshotData changes, recalculate time lapse props */
  useEffect(() => {
    if (!locationsData?.length) {
      return;
    }

    const locDataByTs = groupBy(locationsData, "intervalTimestamp");
    setLocationData(locDataByTs);

    if (!Object.keys(locDataByTs).length) {
      return;
    }

    let markerInterval = 6 * 60 * 60 * 1000;
    const maxTimestamp = parseInt(max(Object.keys(locDataByTs)), 10);
    const minTimestamp = parseInt(min(Object.keys(locDataByTs)), 10);

    if (maxTimestamp - minTimestamp < 12 * 60 * 60 * 1000) {
      markerInterval = 2 * 60 * 60 * 1000;
    } else if (maxTimestamp - minTimestamp < 6 * 60 * 60 * 1000) {
      markerInterval = 60 * 60 * 1000;
    } else if (maxTimestamp - minTimestamp < 60 * 60 * 1000) {
      markerInterval = 15 * 60 * 1000;
    } else {
      markerInterval = maxTimestamp - minTimestamp;
    }

    setMapTimeRange({ minTimestamp, maxTimestamp, markerInterval });
  }, [locationsData, raceType]);

  const getLocationData = useCallback(() => {
    let timeStep = selectedTimeStep;
    let inTimeStep = [];
    let snapshotTimes = Object.keys(locationData);

    // Data has not loaded yet
    if (
      !(snapshotTimes || []).length ||
      !mapTimeRange.minTimestamp ||
      !mapTimeRange.maxTimestamp
    ) {
      return [];
    }

    // Get last snapshot timestamp
    if (snapshotTimes.includes(timeStep)) {
      // timeStep already matches snapshot time
    } else if (timeStep < mapTimeRange.minTimestamp) {
      // timeStep = mapTimeRange.minTimestamp;
      setLastTransmissionDate();
      setCurrentMapState([]);
      return;
    } else if (timeStep > mapTimeRange.maxTimestamp) {
      timeStep = mapTimeRange.maxTimestamp;
    } else {
      // get last snapshot
      timeStep = max(snapshotTimes.filter((ts) => ts <= timeStep));
    }

    inTimeStep = locationData[timeStep];

    if (!(inTimeStep || []).length) {
      setCurrentMapState([]);
      return;
    }

    setLastTransmissionDate(inTimeStep[0].timestamp);

    const withColor = inTimeStep.map((dataPoint) => {
      const list =
        raceType === "presidential" ? presidentialList : vPresidentialList;
      const leadingCandidate = list.find(
        (candidate) => candidate.wp_slug === dataPoint.leadingCandidateSlug
      );
      let leadingCandidateColor = "";
      if (leadingCandidate && leadingCandidate.color) {
        leadingCandidateColor = leadingCandidate.color;
      }
      const location = [...cityList, ...provincesList, ...regionList].find(
        (loc) => loc.slug === dataPoint.locationSlug
      );
      let transmissionStatus = {};

      if (location && location.precincts_count) {
        const percentage =
          getTransmissionPercentage(
            dataPoint.transmissionCount,
            location.precincts_count
          ) || 0;
        const baseColor = [201, 216, 253];
        const maxColor = [80, 85, 139];
        const lerp = (a, b, u) => {
          const colorValue = Math.round((1 - u) * a + u * b);
          return colorValue > 255 ? 255 : colorValue < 0 ? 0 : colorValue;
        };
        const colorStep = baseColor.map((_, index) => {
          const nearestFive = percentage - (percentage % 5);
          const nearestFiveClamped =
            nearestFive < 0 ? 0 : nearestFive > 100 ? 100 : nearestFive;
          return lerp(
            baseColor[index],
            maxColor[index],
            nearestFiveClamped / 100
          );
        });
        transmissionStatus = {
          percentage,
          color: `rgba(${colorStep.join(", ")}, 1)`,
        };
      }

      return {
        ...dataPoint,
        leadingCandidateColor,
        transmissionStatus,
      };
    });

    setCurrentMapState(withColor);
  }, [
    selectedTimeStep,
    locationData,
    mapTimeRange.minTimestamp,
    mapTimeRange.maxTimestamp,
    raceType,
    presidentialList,
    vPresidentialList,
    cityList,
    provincesList,
    regionList,
  ]);

  useEffect(() => {
    if (!timeSliderManuallyChanged) {
      setSelectedTimeStep(mapTimeRange.maxTimestamp);
    }
  }, [mapTimeRange.maxTimestamp, timeSliderManuallyChanged]);

  useEffect(() => {
    if (showCandidateList) {
      const rankingContainer = document.querySelector(
        "[data-id='map-ranking-container'"
      );
      if (!rankingContainer) {
        return;
      }
      rankingContainer.scrollTo({
        behavior: "instant",
        top: 0,
      });
    }
  }, [showCandidateList]);

  useEffect(() => {
    getLocationData();
  }, [getLocationData]);

  useEffect(() => {
    if (!isPlaying) {
      const [currentLocationState] = currentMapState.filter(
        (dataPoint) => dataPoint.locationSlug === locationSlug
      );
      if (currentLocationState && currentLocationState.candidateList) {
        setCurrentCandidateList(currentLocationState.candidateList);
      } else {
        setCurrentCandidateList();
      }
    }
  }, [currentMapState, isPlaying, locationSlug]);

  const handleSliderPause = () => {
    setIsPlaying(false);
    const [currentLocationState] = currentMapState.filter(
      (dataPoint) => dataPoint.locationSlug === locationSlug
    );
    if (currentLocationState && currentLocationState.candidateList) {
      setCurrentCandidateList(currentLocationState.candidateList);
    } else {
      setCurrentCandidateList();
    }
  };

  if (candidateDataError)
    return <p>Something went wrong, Please try again later!</p>;

  /****************************************************************************/
  /* HELPER FUNCTIONS
  /****************************************************************************/
  const loader = ({ src, width, quality }) => {
    const imageUrl = process.env.SITE_URL;
    return `${imageUrl}${src}?w=${width}&q=${quality || 75}`;
  };

  /****************************************************************************/
  /* MAP FUNCTIONS
  /****************************************************************************/
  /* Map Type Selector - can either be "votes" or "transmission-status" */
  const selectMapType = (mapKind) => {
    setMapType(mapKind);
  };

  const handleProvinceClick = ({ regionSlug, provinceSlug, citySlug }) => {
    const city = cityList.find((city) => city.slug === citySlug);
    const province = provincesList.find(
      (province) => province.slug === provinceSlug
    );
    const region = regionList.find((region) => region.slug === regionSlug);

    setSelectedProvinceICity(city || province);
    setSelectedProvinceICityCopy(city || province);
    setProvinceICityFilter(city?.display_name || province?.display_name);

    // if(city) {
    //   setSelectedProvinceICity(city);
    //   setSelectedProvinceICityCopy(city);
    //   setProvinceICityFilter(city.display_name);
    // } else if(province) {
    //   setSelectedProvinceICity(province);
    //   setSelectedProvinceICityCopy(province);
    //   setProvinceICityFilter(province.display_name);
    // }

    if (region && region.slug === "ncr") {
      setSelectedMap("NCR");
    }

    if (region) {
      setSelectedRegion(region);
      setSelectedRegionCopy(region);
      setRegionFilter(`${region.name} - ${region.display_name}`);
    } else {
      // Philippines
      setSelectedRegion(region);
    }
  };

  const handleCityClick = ({ citySlug }) => {
    const city = cityList.find((city) => city.slug === citySlug);
    const region = regionList.find((region) => region.slug === "ncr");

    setSelectedRegion(region);
    setSelectedRegionCopy(region);
    setRegionFilter(`${region.name} - ${region.display_name}`);

    setSelectedProvinceICity(city);
    setSelectedProvinceICityCopy(city);
    setProvinceICityFilter(city.display_name);
  };
  const overrideTooltipPosition = (pos) => {
    const ttwidth = tooltipRef.current.tooltipRef.offsetWidth;
    const ttheight = tooltipRef.current.tooltipRef.offsetHeight;
    return {
      left: mouseCoords.x - ttwidth / 2,
      top: mouseCoords.y - ttheight,
    };
  };
  const updateMouseCoords = (event) => {
    mouseCoords.x = event.clientX;
    mouseCoords.y = event.clientY;
  };
  const handleMapSelect = (mapName) => {
    const region = regionList.find(
      (region) => region.slug.toLowerCase() === mapName.toLowerCase()
    );

    setSelectedMap(mapName);

    if (mapName === "Philippines") {
      onResetFilter();
      return;
    }

    if (mapName === "NCR") {
      setSelectedRegion(region);
    }

    if (mapName === "OAV") {
      scrollToTop();
    }

    setRegionFilter(`${region.name} - ${region.display_name}`);
    setSelectedRegionCopy(region);
    setRegionFilterActive(false);
  };

  const handleProvinceHover = ({ name, location, transmissionPercentage }) => {
    if (name && location && mapType === "transmission-status") {
      setTooltipContent({
        title: name,
        description: `Precincts Transmitted ${transmissionPercentage || 0}%`,
      });
    } else if (name) {
      setTooltipContent({ title: name, description: "" });
    } else {
      setTooltipContent({ title: "", description: "" });
    }
    // ReactTooltip.rebuild();
  };

  const getColorFilter = () => {
    let results = [];
    if (mapType === "votes") {
      const toFilter =
        raceType === "vice-presidential" ? vPresidentialList : presidentialList;
      results = toFilter
        .filter((candidate) => candidate.selected)
        .map((candidate) => candidate.color);
    }

    return results;
  };

  /* Map time slider */
  const handleTimeChange = (selectedTimeStep) => {
    setSelectedTimeStep(selectedTimeStep);
    setTimeSliderManuallyChanged(true);
  };

  /* Search function for Map filters */
  const searchPlace = (query) => {
    const result = miniSearch.search(query, { prefix: true, fuzzy: 0.2 });
    if (result && result.length) {
      return result;
    } else {
      return [];
    }
  };

  const sortById = (a, b) => {
    if (a.id < b.id) {
      return -1;
    }
    if (a.id > b.id) {
      return 1;
    }

    return 0;
  };

  /* Dropdown list of Region Filter */
  const regionSelectionList = () => {
    miniSearch.removeAll();
    if (regionFilter && regionFilter.length >= 2) {
      miniSearch.addAll(regionList);
      return searchPlace(regionFilter).sort(sortById);
    }
    return regionList.sort(sortById);
  };

  /* Dropdown list of Province or Independent City Filter */
  const provinceICitySelectionList = () => {
    miniSearch.removeAll();

    let provinceICityList = [...provincesList, ...cityList];
    provinceICityList = provinceICityList.filter(
      (provinceICity) => provinceICity.region.slug !== "oav"
    );
    if (selectedRegionCopy) {
      provinceICityList = provinceICityList.filter(
        (provinceICity) => provinceICity.region.slug === selectedRegionCopy.slug
      );
      provinceICityList.sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }

        return 0;
      });
    }

    if (provinceICityFilter && provinceICityFilter.length >= 2) {
      miniSearch.addAll(provinceICityList);
      if (selectedRegionCopy?.slug === "ncr") {
        return searchPlace(provinceICityFilter);
      } else {
        return searchPlace(provinceICityFilter).filter(
          (provinceICity) => provinceICity.slug !== "ncr-pateros"
        );
      }
    } else if (selectedRegionCopy) {
      return [...provinceICityList];
    } else {
      return [...provinceICityList].slice(0, 7);
    }
  };

  /* Gets user input for Region filter */
  const onRegionFilter = (event) => {
    const query = event.target.value;
    setRegionFilter(query);
    setRegionFilterActive(true);
    setSelectedRegion(null);
  };

  /* Handler function when user clicks on a Region from the dropdown list */
  const onRegionSelect = (region) => {
    setRegionFilter(`${region.name} - ${region.display_name}`);
    setRegionFilterActive(false);
    setSelectedRegionCopy(region);
    setSelectedProvinceICityCopy(null);
    setProvinceICityFilter("");
    setProvinceICityFilterActive(false);
  };

  /* Gets user input for Province or Independent City filter */
  const onProvinceICityFilter = (event) => {
    const query = event.target.value;
    setProvinceICityFilter(query);
    setProvinceICityFilterActive(true);
    setSelectedProvinceICity(null);
  };

  /* Handler function when user clicks on a Province or Independent City from the dropdown list */
  const onProvinceICitySelect = async (provinceICity) => {
    setRegionFilter(
      `${provinceICity.region.name} - ${provinceICity.region.display_name}`
    );
    setSelectedRegionCopy(provinceICity.region);
    setRegionFilterActive(false);
    setProvinceICityFilter(provinceICity.display_name);
    setSelectedProvinceICityCopy(provinceICity);
    setProvinceICityFilterActive(false);
  };

  /* Map Filter - applies map filters */
  const onFilter = () => {
    setRegionFilterActive(false);
    setProvinceICityFilterActive(false);

    if (selectedRegionCopy.slug === "ncr") {
      setSelectedMap("NCR");
    }

    if (selectedRegionCopy.slug === "oav") {
      setSelectedMap("OAV");
    } else {
      setSelectedRegion(selectedRegionCopy);
      setSelectedProvinceICity(selectedProvinceICityCopy);
    }

    scrollToTop();
  };

  /* Map Filter - clears map filters */
  const onResetFilter = () => {
    setSelectedMap("Philippines");
    setSelectedRegion(null);
    setSelectedRegionCopy(null);
    setRegionFilter("");
    setRegionFilterActive(false);
    setSelectedProvinceICity(null);
    setSelectedProvinceICityCopy(null);
    setProvinceICityFilter("");
    setProvinceICityFilterActive(false);
  };

  const scrollToTop = () => {
    const container = document.getElementById("map-results-container");
    container.scrollIntoView({ behavior: "smooth" });
  };

  /****************************************************************************/
  /* CANDIDATES FUNCTIONS
  /****************************************************************************/
  /* Race Type Selector - can either be "presidential" or "vice-presidential" */
  const selectRaceType = (raceKind) => {
    setRaceType(raceKind);

    if (raceKind === "presidential") {
      setLocationsData([...locationPresData]);
      setSnapshotData([...locationPresData]);
    } else {
      setLocationsData([...locationViceData]);
      setSnapshotData([...locationViceData]);
    }
  };

  /* Handler function when user selected a candidate - can either set a candidate selected state to "true" or "false" */
  const onCandidateSelect = (candidate) => {
    let candidatesArray = [...presidentialList];
    let candidateIndex = 0;

    if (raceType === "vice-presidential") {
      candidatesArray = [...vPresidentialList];
      candidateIndex = 1;
    }

    const toUpdate = candidatesArray.find((item) => item.id === candidate.id);
    toUpdate.selected = !toUpdate.selected;

    if (raceType === "presidential") {
      setPresidentialList({ type: "set", mutation: candidatesArray });
    } else {
      setVPresidentialList({ type: "set", mutation: candidatesArray });
    }

    const filterArray = [...candidateFilter];
    const selectedCount = (candidatesArray || []).filter(
      (candidate) => candidate.selected === true
    ).length;
    if (selectedCount === (candidatesArray || []).length) {
      filterArray[candidateIndex] = "all";
    } else if (
      selectedCount === 5 &&
      (candidatesArray || [])
        .slice(0, 5)
        .filter((candidate) => candidate.selected === true).length === 5
    ) {
      filterArray[candidateIndex] = "top-5";
    } else if (selectedCount === 0) {
      filterArray[candidateIndex] = "clear";
    } else {
      filterArray[candidateIndex] = "custom";
    }
    setCandidateFilter(filterArray);
  };

  /* Candidate Filter (Select All | Clear Selection) - can either set all candidates selected state to "true" or "false" */
  const selectAllCandidates = (selected) => {
    let candidatesArray = [...presidentialList];
    let candidateIndex = 0;

    if (raceType === "vice-presidential") {
      candidatesArray = [...vPresidentialList];
      candidateIndex = 1;
    }

    candidatesArray.forEach((candidate) => (candidate.selected = selected));

    if (raceType === "presidential") {
      setPresidentialList({ type: "set", mutation: candidatesArray });
    } else {
      setVPresidentialList({ type: "set", mutation: candidatesArray });
    }

    if (selected) {
      const filterArray = [...candidateFilter];
      filterArray[candidateIndex] = "all";
      setCandidateFilter(filterArray);
    } else {
      const filterArray = [...candidateFilter];
      filterArray[candidateIndex] = "clear";
      setCandidateFilter(filterArray);
    }
  };

  /* Candidate Filter (Top 5 Candidates) - set selected state of yop 5 candidates to "true" */
  const selectTop5 = () => {
    let candidatesArray = [...presidentialList];
    let candidateIndex = 0;

    if (raceType === "vice-presidential") {
      candidatesArray = [...vPresidentialList];
      candidateIndex = 1;
    }

    candidatesArray.forEach((candidate, index) => {
      if (index < 5) {
        candidate.selected = true;
      } else {
        candidate.selected = false;
      }
    });

    if (raceType === "presidential") {
      setPresidentialList({ type: "set", mutation: candidatesArray });
    } else {
      setVPresidentialList({ type: "set", mutation: candidatesArray });
    }

    const filterArray = [...candidateFilter];
    filterArray[candidateIndex] = "top-5";
    setCandidateFilter(filterArray);
  };

  /****************************************************************************/
  /* CHART FUNCTIONS
  /****************************************************************************/
  // const addHours = (hours, date) => {
  //   date.setTime(date.getTime() + hours * 60 * 60 * 1000);
  //   return date.getTime();
  // }

  // const getCumulativeTimestamps = () => {
  //   const labels = [];
  //   if (cumulativeVotesChartData && cumulativeVotesChartData.length) {
  //     let endDate = cumulativeVotesChartData[cumulativeVotesChartData.length -1].timeMarker;
  //     for (let timeMarker = cumulativeVotesChartData[0].timeMarker; timeMarker <= endDate; timeMarker += TIMELAPSE_INTERVAL_CHART) {
  //       labels.push(getFormattedDateTime(timeMarker));
  //     }
  //   }

  //   return labels;
  // };

  // const getTransmissionTimestamps = () => {
  //   const labels = [];
  //   const filteredSnapshotData = snapshotData.filter(data => data.locationSlug === "philippines");

  //   if (filteredSnapshotData && filteredSnapshotData.length) {
  //     let endDate = parseInt(filteredSnapshotData[filteredSnapshotData.length -1].intervalTimestamp, 10);
  //     for (let timeMarker = parseInt(filteredSnapshotData[0].intervalTimestamp, 10); timeMarker <= endDate; timeMarker += TIMELAPSE_INTERVAL_CHART) {
  //       labels.push(getFormattedDateTime(timeMarker));
  //     }
  //   }
  //   return labels;
  // };

  const cumulativeVotesData = () => {
    const labels = [];
    const datasets = [];

    if (cumulativeVotesChartData && cumulativeVotesChartData.length) {
      let candidateList = [...presidentialList];
      if (raceType === "vice-presidential") {
        candidateList = [...vPresidentialList];
      }
      candidateList.forEach((candidate) => {
        if (candidate.selected) {
          const data = [];
          const timestamps = [];
          const transmissionPercentage = [];
          // push 0 state
          // for testing purposes, added handling to use min timestamp minus 15 minutes if not yet actual transmission
          data.push({
            x:
              cumulativeVotesChartData[0].timestamp <
              ELECTION_DAY_TRANSMISSION_START
                ? new Date(
                    cumulativeVotesChartData[0].timestamp - 15 * 60 * 1000
                  )
                : new Date(ELECTION_DAY_TRANSMISSION_START),
            y: 0,
          });
          timestamps.push(
            getFormattedDateTime(
              cumulativeVotesChartData[0].timestamp <
                ELECTION_DAY_TRANSMISSION_START
                ? new Date(
                    cumulativeVotesChartData[0].timestamp - 15 * 60 * 1000
                  )
                : new Date(ELECTION_DAY_TRANSMISSION_START)
            )
          );
          transmissionPercentage.push("0");

          cumulativeVotesChartData.forEach((cumulativeData) => {
            const candidateData = cumulativeData.data.find(
              (item) =>
                item.candidateSlug.toLowerCase() ===
                candidate.wp_slug.toLowerCase()
            );
            if (candidateData) {
              data.push({
                x: new Date(cumulativeData.timestamp),
                y: candidateData.voteCount,
              });
              timestamps.push(getFormattedDateTime(cumulativeData.timestamp));
              transmissionPercentage.push(
                getTransmissionPercentage(
                  cumulativeData.precinctsTransmitted,
                  TOTAL_PRECINCTS_PH
                )
              );
            }
          });

          datasets.push({
            label: `${candidate.last_name}, ${
              candidate.alternate_name
                ? candidate.alternate_name
                : candidate.first_name
            }`,
            data,
            timestamps,
            rawName: candidate.raw_name,
            thumbnail: candidate.thumbnail,
            transmissionPercentage,
            borderColor: candidate.color,
            borderWidth: 1,
            backgroundColor: candidate.color,
            pointStyle: candidate.legend,
            pointBorderColor: candidate.color,
            pointBorderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 10,
          });
        }
      });
    } else {
      datasets.push({
        label: "",
        data: null,
      });
    }

    return {
      labels,
      datasets,
    };
  };

  const transmissionStatusData = () => {
    const labels = [];
    const datasets = [];

    if (snapshotData && snapshotData.length) {
      let candidateList = [...presidentialList];
      if (raceType === "vice-presidential") {
        candidateList = [...vPresidentialList];
      }

      const locationList = [...regionList];
      if (selectedRegion) {
        locationList = [...provincesList, ...cityList];
        locationList = locationList.filter(
          (location) => location.region.slug === selectedRegion.slug
        );
        locationList.sort((a, b) => {
          if (a.display_name < b.display_name) {
            return -1;
          }
          if (a.display_name > b.display_name) {
            return 1;
          }

          return 0;
        });
      }

      let phDataList = [
        {
          timestamp: new Date().getTime(),
          transmissionCount: 0,
        },
      ];
      phDataList = [
        ...phDataList,
        ...snapshotData.filter((data) => data.locationSlug === "philippines"),
      ];

      // handling for test data: start 15 minutes before first transmission timestamp
      if (phDataList[1] && phDataList[1].timestamp) {
        phDataList[0].timestamp =
          phDataList[1].timestamp < ELECTION_DAY_TRANSMISSION_START
            ? new Date(phDataList[1].timestamp - 60 * 1000 * 15)
            : new Date(ELECTION_DAY_TRANSMISSION_START);
      }

      locationList.forEach((location) => {
        const data = [];
        const transmissionPercentage = [];
        let leadingCandidate = { color: "#172038", legend: "circle" };
        const filteredLocationData = snapshotData.filter(
          (item) =>
            item.locationSlug.toLowerCase() === location.slug.toLowerCase()
        );
        phDataList.forEach((phData, index) => {
          const locationTransmissionData = filteredLocationData.find(
            (locData) => locData.timestamp === phData.timestamp
          );
          if (locationTransmissionData) {
            leadingCandidate = candidateList.find(
              (candidate) =>
                candidate.wp_slug.toLowerCase() ===
                filteredLocationData[
                  filteredLocationData.length - 1
                ].leadingCandidateSlug.toLowerCase()
            );

            transmissionPercentage.push(
              getTransmissionPercentage(
                phData.transmissionCount,
                TOTAL_PRECINCTS_PH
              )
            );
            data.push({
              x: new Date(locationTransmissionData.timestamp),
              y: getTransmissionPercentage(
                locationTransmissionData.transmissionCount,
                location.precincts_count
              ),
            });
          } else {
            if (index === 0) {
              // 0 state
              transmissionPercentage.push("0");
              data.push({
                x:
                  phData.timestamp < ELECTION_DAY_TRANSMISSION_START
                    ? new Date(phData.timestamp)
                    : new Date(ELECTION_DAY_TRANSMISSION_START),
                y: "0",
              });
            } else {
              const prevLocationTransmissionData = filteredLocationData.find(
                (locData) =>
                  locData.timestamp === phDataList[index - 1].timestamp
              );
              data.push({
                x:
                  phData.timestamp < ELECTION_DAY_TRANSMISSION_START
                    ? new Date(phData.timestamp)
                    : new Date(ELECTION_DAY_TRANSMISSION_START),
                y: getTransmissionPercentage(
                  prevLocationTransmissionData?.transmissionCount,
                  location.precincts_count
                ),
              });
            }
          }
        });

        datasets.push({
          label: location.name,
          data,
          transmissionPercentage,
          locationColor: leadingCandidate.color,
          borderColor: leadingCandidate.color,
          borderWidth: 1,
          backgroundColor: leadingCandidate.color,
          pointStyle: leadingCandidate.legend,
          pointBorderColor: leadingCandidate.color,
          pointBorderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 6,
          hitRadius: 5,
        });
      });
    } else {
      datasets.push({
        label: "",
        data: null,
      });
    }

    return {
      labels,
      datasets,
    };
  };

  return (
    <div className={styles.mapResultsContainer} id="map-results-container">
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            2022 {raceType.replace("-", " ")} Race
          </h1>
        </div>
        <div className={styles.description}>
          <div className={styles.content}>
            <p className={styles.description}>
              <span className={styles.bold}>
                {latestResultsPHData && latestResultsPHData.transmissionCount
                  ? getTransmissionPercentage(
                      latestResultsPHData.transmissionCount,
                      TOTAL_PRECINCTS_PH
                    )
                  : "0"}
                %&nbsp;
              </span>{" "}
              precincts reporting as of&nbsp;{" "}
              <span className={styles.bold}>
                {latestResultsPHData && latestResultsPHData.timestamp
                  ? getFriendlyFormattedDateTime(latestResultsPHData.timestamp)
                  : getFriendlyFormattedDateTime(new Date().getTime())}
              </span>
            </p>
            <p className={styles.description}>
              Unofficial-partial results based on real-time data from the
              Comelec transparency server
            </p>
          </div>
          <div className={styles.widget}>
            <WidgetShare
              loader={loader}
              url={`${url}#map-results-container`}
              fbQuote="#PHVoteResults: 2022 Philippine elections interactive, live map  #PHVote #WeDecide"
              tWQuoute="#PHVoteResults: 2022 Philippine elections interactive, live map  #PHVote #WeDecide"
              toolTipId={"toolTip_widget_Default"}
              isMounted={isMounted}
              isEmbedShowed={isEmbedShowed}
              embedUrl={`${basePath}elections/2022/races/embed/graph/full-map`}
            />
          </div>
        </div>
      </div>
      <div className={styles.instructions}>
        <h4 className={styles.title}>Time Slider</h4>
        <p className={styles.description}>
          Press <b>play</b> to see which candidates are leading throughout of
          the transmission period. <b>Drag</b> the slider to see which
          candidates led at a point in time.{" "}
        </p>
      </div>
      <MapTimeSlider
        isPlaying={isPlaying}
        onPlay={() => setIsPlaying(true)}
        onPause={handleSliderPause}
        onChange={handleTimeChange}
        maxTimestamp={mapTimeRange.maxTimestamp}
        minTimestamp={
          // added handling: if data min timestamp is before May 9, 7pm, use data timestamp
          mapTimeRange.minTimestamp < ELECTION_DAY_TRANSMISSION_START
            ? mapTimeRange.minTimestamp - 15 * 60 * 1000
            : ELECTION_DAY_TRANSMISSION_START
        }
        lastTimestamp={lastTransmissionDate}
        markerInterval={mapTimeRange.markerInterval}
        interval={TIMELAPSE_INTERVAL_MAP}
        raceType={raceType}
      />
      <div className={styles.content}>
        <div className={styles.mapContainer}>
          <div className={styles.pills}>
            <div
              className={mapType === "votes" ? styles.active : ""}
              onClick={() => selectMapType("votes")}
            >
              Votes
            </div>
            <div
              className={mapType === "transmission-status" ? styles.active : ""}
              onClick={() => selectMapType("transmission-status")}
            >
              Transmission Status
            </div>
          </div>
          <div className={styles.mapContent} onMouseMove={updateMouseCoords}>
            <div className={styles.mapContentHeader}>
              <h6>Interactive Election Map</h6>
              <div
                role="button"
                className={styles.toggleCandidates}
                onClick={() => setShowCandidateList(!showCandidateList)}
              >
                Show Results
              </div>
              {selectedMap !== "OAV" && selectedRegion && (
                <div className={styles.breadcrumbs}>
                  <span>
                    2022 Elections Map{" "}
                    {`> ${selectedRegion.name} - ${selectedRegion.display_name}`}
                  </span>
                  {selectedProvinceICity && (
                    <span>{` > ${selectedProvinceICity.display_name}`}</span>
                  )}
                </div>
              )}
              {selectedMap === "OAV" && (
                <div className={styles.breadcrumbs}>
                  <span>
                    2022 Elections Map{" "}
                    {`> ${selectedRegionCopy.name} - ${selectedRegionCopy.display_name}`}
                  </span>
                </div>
              )}
            </div>
            <div
              className={`${
                selectedMap === "NCR" ? ` ${styles.mapSwitcherZoomed}` : ""
              }`}
            >
              <MapSwitcher
                selectedMap={selectedMap}
                onMapSelect={handleMapSelect}
                region={selectedRegion}
                province={selectedProvinceICity}
                onProvinceHover={handleProvinceHover}
                onProvinceClick={handleProvinceClick}
                onCityClick={handleCityClick}
                locationData={currentMapState}
                colorFilter={getColorFilter()}
                mapType={mapType}
              />
            </div>
            <ReactTooltip
              overridePosition={overrideTooltipPosition}
              ref={tooltipRef}
            >
              {tooltipContent.title && (
                <div className={styles.mapTooltip}>
                  <p>{tooltipContent.title}</p>
                  <p>{tooltipContent.description}</p>
                </div>
              )}
            </ReactTooltip>
            <p className={styles.iccLabel}>
              Votes for national positions at provincial level include those for
              highly urbanized and independent component cities situated in the
              province.
            </p>
            <div className={styles.filterContainer}>
              <h6>Filter By</h6>
              <input
                className={styles.searchBox}
                name="regionFilter"
                type="text"
                placeholder="Select Region"
                value={regionFilter}
                onChange={onRegionFilter}
                onFocus={() => setRegionFilterActive(true)}
                autoComplete="off"
              />
              {regionFilterActive && (
                <div className={styles.filterSelection}>
                  {regionSelectionList().length
                    ? regionSelectionList().map((region) => {
                        return (
                          <div
                            key={region.id}
                            className={styles.filterSelectionItem}
                            onClick={() => onRegionSelect(region)}
                          >
                            <span>{`${region.name} - ${region.display_name}`}</span>
                          </div>
                        );
                      })
                    : "No results found."}
                </div>
              )}
              <input
                className={`${styles.searchBox} ${
                  selectedRegionCopy?.name === "OAV" ? styles.disabled : ""
                }`}
                name="provinceICityFilter"
                type="text"
                placeholder={
                  selectedRegionCopy && selectedRegionCopy.slug === "ncr"
                    ? "Select City or Municipality"
                    : "Select Province or Independent City"
                }
                value={provinceICityFilter}
                onChange={onProvinceICityFilter}
                onFocus={() => setProvinceICityFilterActive(true)}
                autoComplete="off"
                disabled={selectedRegionCopy?.name === "OAV" ? true : false}
              />
              {provinceICityFilterActive && (
                <div className={styles.filterSelection}>
                  {provinceICitySelectionList().length
                    ? provinceICitySelectionList().map((provinceICity) => {
                        return (
                          <div
                            key={provinceICity.id}
                            className={styles.filterSelectionItem}
                            onClick={() => onProvinceICitySelect(provinceICity)}
                          >
                            <span>{provinceICity.display_name}</span>
                          </div>
                        );
                      })
                    : "No results found."}
                </div>
              )}
              <div className={styles.buttonContainer}>
                <button
                  className={
                    selectedMap === "OAV" ||
                    selectedRegionCopy !== null ||
                    selectedProvinceICityCopy !== null
                      ? styles.primary
                      : styles.disabled
                  }
                  onClick={onFilter}
                >
                  Filter
                </button>
                <button onClick={onResetFilter}>Reset Filter</button>
              </div>
            </div>
          </div>
        </div>
        <div
          data-id="map-ranking-container"
          className={`${styles.rankingContainer} ${
            showCandidateList ? styles.open : ""
          }`}
        >
          <div className={styles.rankingHeader}>
            <div className={styles.pills}>
              <div
                className={raceType === "presidential" ? styles.active : ""}
                onClick={() => selectRaceType("presidential")}
              >
                President
              </div>
              <div
                className={
                  raceType === "vice-presidential" ? styles.active : ""
                }
                onClick={() => selectRaceType("vice-presidential")}
              >
                Vice President
              </div>
            </div>
            <div
              className={styles.closeRanking}
              onClick={() => setShowCandidateList(!showCandidateList)}
            >
              <Image
                loader={loader}
                src={CloseIcon}
                alt="close-rankings"
                width={17.49}
                height={17.49}
              />
            </div>
          </div>
          <MapResultsCandidates
            location={
              selectedMap === "OAV"
                ? `${selectedRegionCopy.name} - ${selectedRegionCopy.display_name}`
                : selectedProvinceICity
                ? selectedProvinceICity.display_name
                : selectedRegion
                ? `${selectedRegion.name} - ${selectedRegion.display_name}`
                : "Philippines"
            }
            candidatesList={
              raceType === "presidential" ? presidentialList : vPresidentialList
            }
            contestName={
              raceType === "presidential" ? "President" : "Vice President"
            }
            candidateFilter={candidateFilter}
            selectAllCandidates={selectAllCandidates}
            selectTop5={selectTop5}
            timestamp={
              latestResultsPHData && latestResultsPHData.timestamp
                ? getFriendlyFormattedDateTime(latestResultsPHData.timestamp)
                : getFriendlyFormattedDateTime(new Date().getTime())
            }
            voteCount={
              latestResultsOAVData && latestResultsOAVData.voteCount
                ? latestResultsOAVData.voteCount
                : 0
            }
            transmissionPercentage={
              latestResultsOAVData && latestResultsOAVData.transmissionCount
                ? getTransmissionPercentage(
                    latestResultsOAVData.transmissionCount,
                    TOTAL_PRECINCTS_OAV
                  )
                : 0
            }
            onCandidateSelect={onCandidateSelect}
          />
          <div className={styles.chartContainer}>
            {mapType === "votes" ? (
              <MapCumulativeChart
                dataChart={cumulativeVotesData()}
                title="Cumulative Votes per Candidate"
                description="Philippines + OAV votes"
              />
            ) : (
              <MapTransmissionChart
                dataChart={transmissionStatusData()}
                place={selectedRegion ? "Province/Independent City" : "Region"}
              />
            )}
          </div>
          <MapChartLegend
            candidatesList={
              raceType === "presidential"
                ? presidential.results
                : vPresidential.results
            }
          />
        </div>
      </div>
    </div>
  );
};

export default MapResults;
